import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import { Settings, Users, Database, Shield, Bell, Palette, Bot, MapPin, Book, MessageSquare, Brain } from 'lucide-react'
import UserManagement from '@/components/settings/UserManagement'
import AgentVersionManagement from '@/components/settings/AgentVersionManagement'
import UserLocationDisplay from '@/components/settings/UserLocationDisplay'
import FeedbackManagement from '@/components/settings/FeedbackManagement'
import StoreMemoryAdmin from '@/components/settings/StoreMemoryAdmin'

const SettingsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth)
  const { tab } = useParams<{ tab?: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(tab || 'general')

  // Update activeTab when URL parameter changes
  useEffect(() => {
    setActiveTab(tab || 'general')
  }, [tab])

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'feedback', label: 'Feedback Management', icon: MessageSquare },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'store-memory', label: 'Store Memory', icon: Brain },
    { id: 'agents', label: 'Agent Versions', icon: Bot },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
  ]

  const TabIcon = tabs.find(tab => tab.id === activeTab)?.icon || Settings

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">System Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure system-wide settings and preferences
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Logged in as: <span className="font-medium">{user?.firstName} {user?.lastName}</span> ({user?.role})
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-card rounded-lg border border-border p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        if (tab.id === 'general') {
                          navigate('/settings')
                        } else {
                          navigate(`/settings/${tab.id}`)
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === tab.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-card rounded-lg border border-border">
              {/* Tab Header */}
              <div className="px-6 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <TabIcon className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">
                    {tabs.find(tab => tab.id === activeTab)?.label}
                  </h2>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'general' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">General Settings</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            System Name
                          </label>
                          <input
                            type="text"
                            defaultValue="RWB Agent System"
                            className="w-full max-w-md px-3 py-2 border border-input bg-background rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Default Language
                          </label>
                          <select className="w-full max-w-md px-3 py-2 border border-input bg-background rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary">
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Glossary Management Section */}
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">Business Glossary</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-4">
                            Manage business terminology and definitions used throughout the system.
                          </p>
                          <button
                            onClick={() => navigate('/glossary')}
                            className="inline-flex items-center px-4 py-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md text-sm font-medium transition-colors"
                          >
                            <Book className="h-4 w-4 mr-2" />
                            Open Glossary Management
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'users' && (
                  <UserManagement />
                )}

                {activeTab === 'feedback' && (
                  <FeedbackManagement />
                )}

                {activeTab === 'locations' && (
                  <UserLocationDisplay />
                )}

                {activeTab === 'agents' && (
                  <AgentVersionManagement />
                )}

                {activeTab === 'store-memory' && (
                  <StoreMemoryAdmin />
                )}

                {activeTab === 'database' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">Database Management</h3>
                      <p className="text-muted-foreground mb-4">
                        Database maintenance, backups, and optimization settings.
                      </p>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground">
                          Database management tools will be implemented here.
                          This will include backup scheduling, maintenance tasks, and performance monitoring.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'security' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">Security Settings</h3>
                      <p className="text-muted-foreground mb-4">
                        Configure authentication, authorization, and security policies.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-foreground">
                              Require 2FA for all users
                            </label>
                            <p className="text-xs text-muted-foreground">
                              Force all users to enable two-factor authentication
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-foreground">
                              Session timeout
                            </label>
                            <p className="text-xs text-muted-foreground">
                              Automatically log out inactive users
                            </p>
                          </div>
                          <select className="px-3 py-1 border border-input bg-background rounded text-sm">
                            <option value="30">30 minutes</option>
                            <option value="60">1 hour</option>
                            <option value="120">2 hours</option>
                            <option value="480">8 hours</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'notifications' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">Notification Settings</h3>
                      <p className="text-muted-foreground mb-4">
                        Configure system notifications and alerts.
                      </p>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground">
                          Notification preferences will be implemented here.
                          This will include email notifications, in-app alerts, and system notifications.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'appearance' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">Appearance Settings</h3>
                      <p className="text-muted-foreground mb-4">
                        Customize the look and feel of the system.
                      </p>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground">
                          Theme and appearance customization will be implemented here.
                          This will include color schemes, layout options, and branding settings.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage 